import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  AuthService,
  AutoLoginService,
  Installation,
  QbusLocaleService,
} from '@ubie/core';

const DISPLAYED_COLUMNS: string[] = [
  'name',
  'address',
  'owner',
  'added',
  'actions',
];

@Component({
  selector: 'acl-installation-list',
  standalone: false,
  templateUrl: './installation-list.component.html',
  styleUrl: './installation-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly autoLoginService = inject(AutoLoginService);
  private readonly qbusLocaleService = inject(QbusLocaleService);

  readonly installations = input<Installation[] | undefined>(undefined);
  readonly hasInstallation = computed(
    () => (this.installations() ?? []).length > 0,
  );
  readonly installationCount = computed(
    () => (this.installations() ?? []).length,
  );
  readonly columns = input<any[]>();

  readonly displayedColumns = DISPLAYED_COLUMNS;

  readonly dataSource = new MatTableDataSource<Installation>();

  @ViewChild(MatPaginator)
  set paginator(paginator: MatPaginator | undefined) {
    if (!paginator) {
      return;
    }
    this.dataSource.paginator = paginator;
  }

  @ViewChild(MatSort)
  set sort(sort: MatSort | undefined) {
    if (!sort) {
      return;
    }
    this.dataSource.sort = sort;
  }

  private readonly syncDataEffect = effect(() => {
    const data = this.installations() ?? [];
    this.dataSource.data = data;
  });

  ngOnInit(): void {
    this.dataSource.filterPredicate = this.installationFilterPredicate;
    this.dataSource.sortingDataAccessor = this.installationSortingDataAccessor;
  }

  private installationFilterPredicate(
    data: Installation,
    filter: string,
  ): boolean {
    const term = filter.trim().toLowerCase();
    if (!term) {
      return true;
    }
    const fields: (string | undefined)[] = [
      data.name,
      (data as any).addressLine1,
      (data as any).postalCode,
      (data as any).city,
      data.owner?.firstname,
      data.owner?.lastname,
    ];
    return fields
      .filter((field): field is string => Boolean(field))
      .join(' ')
      .toLowerCase()
      .includes(term);
  }

  private installationSortingDataAccessor(
    data: Installation,
    sortHeaderId: string,
  ): string | number {
    switch (sortHeaderId) {
      case 'name':
        return (data.name ?? '').toLowerCase();
      case 'address': {
        const address =
          `${(data as any).addressLine1 ?? ''} ${(data as any).postalCode ?? ''} ${(data as any).city ?? ''}`.trim();
        return address.toLowerCase();
      }
      case 'owner': {
        const owner = data.owner
          ? `${(data.owner as any).firstname ?? ''} ${(data.owner as any).lastname ?? ''}`.trim()
          : '';
        return owner.toLowerCase();
      }
      case 'added':
        return data.added ? new Date(data.added as any).getTime() : 0;
      default:
        return 0;
    }
  }

  applyFilter(value: string): void {
    this.dataSource.filter = value.trim().toLowerCase();
  }

  onInstallationClicked(installation_id: number): void {
    this.router.navigate(['/installation/' + installation_id + '/detail']);
  }

  getAutoLoginLabel(row: Installation): string | null {
    const hasInstallerVisualisationAccess = this.hasAutoLoginAccess(row);

    if (!hasInstallerVisualisationAccess) {
      return null;
    }

    return row.originId === 2
      ? 'installation_loginQcp'
      : 'installation_loginQc';
  }

  private hasAutoLoginAccess(row: Installation): boolean {
    const ownerId = row.owner?.id ?? 0;
    const ownerConfirmed = Boolean((row as any).ownerIdConfirmed);
    if (ownerId <= 0 || !ownerConfirmed) {
      return false;
    }

    this.authService.getUserId();
    if (this.authService.hasClaim('CanAccessInstallationsAll')) {
      return true;
    }

    const isInstaller2ForRow = this.isCurrentUserInstaller2ForRow(row);
    const accessFlag = isInstaller2ForRow
      ? row.installer2VisualisationAccess
      : row.installerVisualisationAccess;

    return Boolean(accessFlag);
  }

  private isCurrentUserInstaller2ForRow(row: Installation): boolean {
    const currentUserId = this.authService.getUserId();
    if (!currentUserId) {
      return false;
    }

    return row.installer2?.id === currentUserId;
  }

  formatAddedDate(dateValue: string | Date | undefined): string {
    const date = dateValue ? new Date(dateValue) : null;
    return date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(this.qbusLocaleService.locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';
  }

  onAutoLogin(row: Installation): void {
    this.autoLoginService.getQbusControlAutoLoginUrl(row.id).subscribe({
      next: (url) => {
        window.open(url, '_blank');
      },
      error: () => {
        alert('Failed to get auto-login URL.');
      },
    });
  }
}
